import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetBackdateGrantsQuery,
  useGetEmployeeStaffReportsQuery,
  useGetTechnicalEmployeesQuery,
} from "../../../store/appApi";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import {
  createBackdateGrantApi,
  editStaffReportWorkUpdateApi,
  revokeBackdateGrantApi,
} from "../../../services/staffReporting";
import {
  formatDateLabel,
  formatRoleLabel,
  getWorkOverdueInfo,
  isWorkVerified,
  normalizeFieldReport,
  normalizeRemarksHistory,
  normalizeSubWorkUpdates,
  reportHasOverdueWork,
  sortSubWorkUpdates,
  toDateInputValue,
  workBadgeToneClass,
} from "./reportingConfig";
import { exportEmployeeReportsToExcel } from "./exportStaffReportExcel";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

// Only these roles get the newer sub-work breakdown table below — every
// other role keeps the original card list untouched.
const TECHNICAL_ROLES = ["Civil Engineer", "Project Manager", "Supervisor"];

const cardStatusChip = (report) => {
  if (report.isComplete)
    return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
  if (/review/i.test(String(report.status || "")))
    return "bg-executive-50 text-executive-700 ring-1 ring-executive-200";
  return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
};

// Work is finished — status reads "done", "complete" or "completed".
const isDoneStatus = (status) => /(done|complete)/i.test(String(status || ""));

// A derived unit/main/sub tree node keeps its dated activity as `updates`
// (sub) or as nested `subs` (main), not the raw `updatedRemarks` /`subWorks`
// the overdue helper expects — bridge the shapes here.
const overdueInfoForNode = (node) =>
  getWorkOverdueInfo({
    status: node.status,
    completionDate: node.completionDate,
    extendedDate: node.extendedDate,
    completedOn: node.completedOn,
    verification: node.verification,
    isOnHold: node.isOnHold,
    holdReason: node.holdReason,
    holdDate: node.holdDate,
    // A main node carries its finished sub-works so the helper can read their
    // completedOn; a sub node carries its own dated updates.
    subWorks: Array.isArray(node.subs)
      ? node.subs.map((sub) => ({
          completedOn: sub.completedOn,
          updatedRemarks: sub.updates || [],
        }))
      : undefined,
    updatedRemarks: Array.isArray(node.updates) ? node.updates : [],
  });

// Deadline pill: red once a deadline is crossed with the work still open, amber
// when the work was finished late or is due within 3 days, slate while on track.
const OverdueBadge = ({ node }) => {
  const info = overdueInfoForNode(node);
  if (!info || !info.label) return null;
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${workBadgeToneClass(
        info.state,
      )}`}
    >
      <i
        className={`fa-solid ${
          info.state === "overdue" || info.state === "completed-late"
            ? "fa-triangle-exclamation"
            : "fa-clock"
        } text-[10px]`}
      />
      {info.label}
    </span>
  );
};

// Sky pill when an admin pushed the deadline out: "Deadline 5 Nov → 7 Nov (+2d)".
const ExtendedBadge = ({ node }) => {
  const info = overdueInfoForNode(node);
  if (!info || !info.extended || !info.extendedDate) return null;
  const was = node.completionDate ? `${formatDateLabel(node.completionDate)} → ` : "";
  return (
    <span
      className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-200"
      title={
        info.extensionCount > 1 ? `Extended ${info.extensionCount} times` : undefined
      }
    >
      <i className="fa-solid fa-calendar-plus text-[10px]" />
      Extended: {was}
      {formatDateLabel(info.extendedDate)}
      {info.extensionDays ? ` (+${info.extensionDays}d)` : ""}
    </span>
  );
};

// Green pill once an admin has signed off on a completed work.
const VerifiedBadge = ({ node }) => {
  if (!isWorkVerified(node)) return null;
  const { verifiedByName, verifiedAt, remarks } = node.verification;
  const title = [
    verifiedByName && `By ${verifiedByName}`,
    verifiedAt && formatDateLabel(verifiedAt),
    remarks && `“${remarks}”`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span
      title={title || undefined}
      className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200"
    >
      <i className="fa-solid fa-circle-check text-[10px]" />
      Verified
    </span>
  );
};

// Professional "Well done" badge — work finished on/before its deadline and
// verified by an admin.
const WellDoneBadge = ({ node }) => {
  if (!overdueInfoForNode(node)?.wellDone) return null;
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
      <i className="fa-solid fa-award text-[10px]" />
      Well Done
    </span>
  );
};

// A main work reads "On Hold" when it is paused itself, or when every
// sub-work that is still open is paused. Reason falls back to the subs' reasons.
const deriveMainHold = (main, subs) => {
  const open = subs.filter((x) => !isDoneStatus(x.status));
  const held = open.filter((x) => x.isOnHold);
  const isOnHold =
    Boolean(main.isOnHold) || (open.length > 0 && held.length === open.length);
  const holdReason =
    main.holdReason ||
    held
      .map((x) => (x.holdReason ? `${x.name}: ${x.holdReason}` : ""))
      .filter(Boolean)
      .join(" · ");
  return { isOnHold, holdReason };
};

// Status text shown in a chip — a paused work is "On Hold", not "Pending".
const shownStatus = (node) =>
  node?.isOnHold && !isDoneStatus(node.status) ? "On Hold" : node?.status;

const statusChip = (status) => {
  if (/on hold/i.test(String(status || "")))
    return "bg-blue-50 text-blue-700 ring-1 ring-blue-200";
  if (/complete/i.test(String(status || "")))
    return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
  if (/review/i.test(String(status || "")))
    return "bg-executive-50 text-executive-700 ring-1 ring-executive-200";
  return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
};

const statusDot = (status) => {
  if (/on hold/i.test(String(status || ""))) return "bg-blue-500";
  if (/complete/i.test(String(status || ""))) return "bg-emerald-500";
  if (/review/i.test(String(status || ""))) return "bg-executive-500";
  if (/progress/i.test(String(status || ""))) return "bg-sky-500";
  return "bg-gray-400";
};

const completionBarColor = (percent) => {
  if (percent >= 100) return "bg-emerald-500";
  if (percent >= 50) return "bg-sky-500";
  if (percent > 0) return "bg-executive-500";
  return "bg-gray-300";
};

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const workSummary = (report) => {
  const formData = report.formData || {};

  const work = formData.work;
  const subWorks = Array.isArray(formData.subWorks) ? formData.subWorks : [];
  if (work || subWorks.length) {
    const subWorkNames = subWorks
      .map((item) => item?.subWorkName)
      .filter(Boolean);
    return [work, subWorkNames.join(", ")].filter(Boolean).join(" — ");
  }

  const tasks = Array.isArray(formData.tasks) ? formData.tasks : [];
  if (tasks.length) {
    const taskNames = tasks.map((item) => item?.work).filter(Boolean);
    return taskNames.join(", ");
  }

  return "";
};

// The TRUE last-updated date: the timestamp of the most recent real edit in
// the history log. history[0] is the auto "Created …" record, so only entries
// after it count. Returns null when the sheet has never been edited
// (`lastEditedAt` / `updatedAt` can't be trusted — they default to creation
// time and get bumped by unrelated writes).
const lastRealUpdate = (report) => {
  const history = Array.isArray(report?.history) ? report.history : [];
  const edits = history.slice(1);
  if (!edits.length) return null;
  return edits[edits.length - 1]?.changedAt || null;
};

// The most recent dated note across a formType 5/6 sheet's tasks and their
// nested sub-works, so the list can show what actually changed.
const latestNote = (report) => {
  const tasks = Array.isArray(report?.formData?.tasks)
    ? report.formData.tasks
    : [];
  const notes = [];
  tasks.forEach((task) => {
    normalizeRemarksHistory(task?.updatedRemarks).forEach((n) => notes.push(n));
    (Array.isArray(task?.subWorks) ? task.subWorks : []).forEach((sub) =>
      normalizeRemarksHistory(sub?.updatedRemarks).forEach((n) => notes.push(n)),
    );
  });
  if (!notes.length) return null;
  return notes.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))[0];
};

// The sheet's unit: the explicit formData.unitName when set, otherwise
// recovered from the periodKey ("unit:<name>") so older sheets that only
// carry the key still surface a unit in the list + filter.
const unitNameOf = (report) => {
  const explicit = String(report?.formData?.unitName || "").trim();
  if (explicit) return explicit;
  const periodKey = String(report?.periodKey || "");
  return periodKey.startsWith("unit:") ? periodKey.slice(5).trim() : "";
};

// No per-row completion figure is stored — approximate it from status the
// same way Detail.jsx's Sub Works table does.
const fieldCompletion = (status) => {
  if (/complete/i.test(String(status || ""))) return 100;
  if (/progress/i.test(String(status || ""))) return 50;
  return 0;
};

// A main work's shown status is derived from its sub-works (so the chip can
// never say "Pending" while the bar reads 100%). Falls back to its own stored
// status only when it has no sub-works.
const rollUpStatus = (subs, fallback) => {
  const list = Array.isArray(subs) ? subs : [];
  if (!list.length) return fallback || "Pending";
  if (list.every((s) => /complete/i.test(String(s.status || ""))))
    return "Complete";
  if (list.some((s) => /complete|progress/i.test(String(s.status || ""))))
    return "In Progress";
  return "Pending";
};

const latestOf = (dates) =>
  dates
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0] || null;

// Fold every Field Execution Journal sheet into a Unit → Main Work → Sub Work
// tree. A unit can span more than one sheet (rare, but legacy sheets keyed by
// date share a unit name); main works / sub-works are merged by name and each
// sub-work keeps its full dated update log, newest first.
const buildUnitTree = (reports) => {
  const units = new Map();

  reports.forEach((report) => {
    const unitName = unitNameOf(report) || "Unassigned unit";
    const uKey = unitName.toLowerCase();
    if (!units.has(uKey)) {
      units.set(uKey, {
        key: uKey,
        name: unitName,
        reportId: report._id,
        reportDate: report.reportDate,
        mains: new Map(),
      });
    }
    const unit = units.get(uKey);
    if (new Date(report.reportDate || 0) >= new Date(unit.reportDate || 0)) {
      unit.reportDate = report.reportDate;
      unit.reportId = report._id;
    }

    const field = normalizeFieldReport(report.formData);
    field.mainWorks.forEach((main) => {
      const mName = main.mainWorkName || "Untitled work";
      const mKey = mName.toLowerCase();
      if (!unit.mains.has(mKey)) {
        unit.mains.set(mKey, {
          key: `${uKey}::${mKey}`,
          name: mName,
          reportId: report._id,
          status: main.status || "Pending",
          startDate: main.startDate || "",
          completionDate: main.completionDate || "",
          extendedDate: main.extendedDate || "",
          completedOn: main.completedOn || "",
          verification: main.verification || null,
          isOnHold: Boolean(main.isOnHold),
          holdReason: main.holdReason || "",
          holdDate: main.holdDate || "",
          subs: new Map(),
        });
      }
      const mainNode = unit.mains.get(mKey);
      if (main.status) mainNode.status = main.status;
      if (main.extendedDate) mainNode.extendedDate = main.extendedDate;
      if (main.completedOn) mainNode.completedOn = main.completedOn;
      if (main.verification) mainNode.verification = main.verification;
      mainNode.isOnHold = Boolean(main.isOnHold);
      mainNode.holdReason = main.holdReason || "";
      mainNode.holdDate = main.holdDate || "";

      (main.subWorks || []).forEach((sub) => {
        const sName = sub.subWorkName || "Untitled sub-work";
        const sKey = sName.toLowerCase();
        // Each update remembers the sheet / work it lives on, so it can be
        // edited from here even when the unit spans several sheets.
        const updates = sortSubWorkUpdates(sub.updatedRemarks, {
          newestFirst: true,
        }).map((entry) => ({
          ...entry,
          reportId: report._id,
          reportComplete: Boolean(report.isComplete),
          mainId: main.id,
          subId: sub.id,
        }));
        const people = normalizeSubWorkUpdates(sub.updatedRemarks).reduce(
          (sum, entry) =>
            sum +
            (entry.peopleCount === "" ? 0 : Number(entry.peopleCount) || 0),
          0,
        );
        const existing = mainNode.subs.get(sKey);
        if (existing) {
          existing.updates = [...existing.updates, ...updates].sort(
            (a, b) => new Date(b.date || 0) - new Date(a.date || 0),
          );
          existing.people += people;
          if (sub.status) existing.status = sub.status;
          if (sub.extendedDate) existing.extendedDate = sub.extendedDate;
          if (sub.completedOn) existing.completedOn = sub.completedOn;
          if (sub.verification) existing.verification = sub.verification;
          existing.remarks = existing.remarks || sub.remarks || "";
          existing.isOnHold = Boolean(sub.isOnHold);
          existing.holdReason = sub.holdReason || "";
          existing.holdDate = sub.holdDate || "";
        } else {
          mainNode.subs.set(sKey, {
            key: `${mainNode.key}::${sKey}`,
            name: sName,
            status: sub.status || "Pending",
            startDate: sub.startDate || "",
            completionDate: sub.completionDate || "",
            extendedDate: sub.extendedDate || "",
            completedOn: sub.completedOn || "",
            remarks: sub.remarks || "",
            verification: sub.verification || null,
            isOnHold: Boolean(sub.isOnHold),
            holdReason: sub.holdReason || "",
            holdDate: sub.holdDate || "",
            people,
            updates,
          });
        }
      });
    });
  });

  return Array.from(units.values())
    .map((unit) => {
      const mains = Array.from(unit.mains.values())
        .map((main) => {
          const subs = Array.from(main.subs.values());
          const completionPercent = subs.length
            ? Math.round(
                subs.reduce((s, x) => s + fieldCompletion(x.status), 0) /
                  subs.length,
              )
            : fieldCompletion(main.status);
          const latestUpdate = latestOf(
            subs.flatMap((s) => s.updates.map((u) => u.date)),
          );
          // Total manpower for this main work — every one of its sub-works'
          // head-counts added up.
          const people = subs.reduce((s, x) => s + (x.people || 0), 0);
          return {
            ...main,
            ...deriveMainHold(main, subs),
            subs,
            status:
              completionPercent >= 100
                ? "Complete"
                : rollUpStatus(subs, main.status),
            completionPercent,
            latestUpdate,
            people,
          };
        })
        .sort(
          (a, b) =>
            new Date(b.latestUpdate || 0) - new Date(a.latestUpdate || 0),
        );

      const completionPercent = mains.length
        ? Math.round(
            mains.reduce((s, m) => s + m.completionPercent, 0) / mains.length,
          )
        : 0;
      const latestUpdate =
        latestOf(mains.map((m) => m.latestUpdate)) || unit.reportDate;
      const subCount = mains.reduce((s, m) => s + m.subs.length, 0);
      return { ...unit, mains, completionPercent, latestUpdate, subCount };
    })
    .sort(
      (a, b) => new Date(b.latestUpdate || 0) - new Date(a.latestUpdate || 0),
    );
};

const EmployeeStaffReports = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGetEmployeeStaffReportsQuery(employeeId, {
    skip: !employeeId,
    // Always pull fresh on entry so a sheet just created on the Create page
    // shows up here instead of a stale cached list.
    refetchOnMountOrArgChange: true,
  });
  const { data: technicalData } = useGetTechnicalEmployeesQuery();
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const currentUser = useSelector(selectUser);
  // The employee (or a super-admin) can correct a wrongly-added daily update.
  const canEditUpdates =
    isSuperAdmin || String(employeeId) === String(currentUser?.localId || "");
  // The daily update being corrected: { key, date, text, people }.
  const [updateEdit, setUpdateEdit] = useState(null);
  const [updateSaving, setUpdateSaving] = useState(false);

  const saveWorkUpdate = async (u, { remove = false } = {}) => {
    if (remove) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Delete this update?",
        text: `${formatDateLabel(u.date)} — ${u.text || "people count only"}`,
        showCancelButton: true,
        confirmButtonText: "Delete",
      });
      if (!confirm.isConfirmed) return;
    } else if (
      !String(updateEdit?.text || "").trim() &&
      String(updateEdit?.people ?? "").trim() === ""
    ) {
      Swal.fire({
        icon: "warning",
        title: "Nothing to save",
        text: "Enter a note or a people count, or delete the update instead.",
      });
      return;
    }
    try {
      setUpdateSaving(true);
      await editStaffReportWorkUpdateApi(u.reportId, {
        mainId: u.mainId,
        subId: u.subId,
        original: { date: u.date, text: u.text, peopleCount: u.peopleCount },
        text: remove ? u.text : String(updateEdit.text || "").trim(),
        peopleCount: remove
          ? u.peopleCount
          : String(updateEdit.people ?? "").trim(),
        date: remove ? undefined : updateEdit.date || undefined,
        remove,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      await refetch();
      setUpdateEdit(null);
      Swal.fire({
        icon: "success",
        title: remove ? "Update deleted" : "Update corrected",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message || "Could not change this update.",
      });
    } finally {
      setUpdateSaving(false);
    }
  };
  // Super-admin only: let this employee add reports for a past day.
  const [allowOpen, setAllowOpen] = useState(false);
  const [allowDate, setAllowDate] = useState("");
  const [allowToDate, setAllowToDate] = useState("");
  const [allowNote, setAllowNote] = useState("");
  const [allowBusy, setAllowBusy] = useState(false);
  const { data: grantData } = useGetBackdateGrantsQuery(employeeId, {
    skip: !isSuperAdmin || !employeeId,
  });
  const grants = Array.isArray(grantData?.grants) ? grantData.grants : [];
  const todayKey = new Date().toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });

  const submitGrant = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(allowDate)) {
      Swal.fire({ icon: "warning", title: "Pick a date first" });
      return;
    }
    setAllowBusy(true);
    try {
      await createBackdateGrantApi({
        employeeId,
        date: allowDate,
        toDate: allowToDate || allowDate,
        note: allowNote.trim(),
      });
      dispatch(appApi.util.invalidateTags(["BackdateGrants"]));
      setAllowDate("");
      setAllowToDate("");
      setAllowNote("");
      Swal.fire({
        icon: "success",
        title: "Allowed",
        text: "The employee can now add reports for the selected date(s). The permission expires in 48 hours.",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Couldn't allow",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setAllowBusy(false);
    }
  };

  const revokeGrant = async (id) => {
    try {
      await revokeBackdateGrantApi(id);
      dispatch(appApi.util.invalidateTags(["BackdateGrants"]));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Couldn't remove",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    }
  };
  const [exporting, setExporting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openUnits, setOpenUnits] = useState(() => new Set());
  const [openMains, setOpenMains] = useState(() => new Set());

  const employee = data?.employee || null;
  const summary = data?.summary || {};
  const isTechnicalEmployee = TECHNICAL_ROLES.includes(employee?.role);

  const allReports = useMemo(
    () => (Array.isArray(data?.reports) ? data.reports : []),
    [data],
  );
  const unitTree = useMemo(() => buildUnitTree(allReports), [allReports]);

  const unitOptions = unitTree.map((unit) => unit.name);
  const statusOptions = Array.from(
    new Set(
      unitTree.flatMap((unit) =>
        unit.mains.flatMap((main) => [
          main.status,
          ...main.subs.map((sub) => sub.status),
        ]),
      ),
    ),
  )
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  // ── Filter/search across every level: unit, main work, sub-work, update ──
  const q = searchText.trim().toLowerCase();
  const textHit = (value) => String(value || "").toLowerCase().includes(q);

  const subPasses = (sub) => {
    if (statusFilter && sub.status !== statusFilter) return false;
    if (!q) return true;
    return (
      textHit(sub.name) ||
      textHit(sub.status) ||
      textHit(sub.remarks) ||
      sub.updates.some(
        (u) => textHit(u.text) || textHit(formatDateLabel(u.date)),
      )
    );
  };

  // Recompute a unit/main's roll-ups from whatever survived filtering so the
  // header counts, sub-counts and % on screen always match what's shown.
  const rescoreMain = (main) => {
    const completionPercent = main.subs.length
      ? Math.round(
          main.subs.reduce((s, x) => s + fieldCompletion(x.status), 0) /
            main.subs.length,
        )
      : fieldCompletion(main.status);
    return {
      ...main,
      ...deriveMainHold(main, main.subs),
      status:
        completionPercent >= 100
          ? "Complete"
          : rollUpStatus(main.subs, main.status),
      completionPercent,
      latestUpdate: latestOf(
        main.subs.flatMap((s) => s.updates.map((u) => u.date)),
      ),
    };
  };
  const rescoreUnit = (unit) => {
    const subCount = unit.mains.reduce((n, m) => n + m.subs.length, 0);
    return {
      ...unit,
      subCount,
      completionPercent: unit.mains.length
        ? Math.round(
            unit.mains.reduce((s, m) => s + m.completionPercent, 0) /
              unit.mains.length,
          )
        : 0,
      latestUpdate:
        latestOf(unit.mains.map((m) => m.latestUpdate)) || unit.reportDate,
    };
  };

  const visibleUnits = unitTree
    .filter((unit) => !unitFilter || unit.name === unitFilter)
    .map((unit) => {
      const unitNameMatch = !statusFilter && q && textHit(unit.name);
      const mains = unit.mains
        .map((main) => {
          const mainNameMatch =
            !statusFilter && q && (textHit(main.name) || textHit(main.status));
          const subs = main.subs.filter(subPasses);
          if (subs.length) return rescoreMain({ ...main, subs });
          if (mainNameMatch || unitNameMatch || (!q && !statusFilter)) {
            return rescoreMain({ ...main, subs: main.subs });
          }
          return null;
        })
        .filter(Boolean);
      if (mains.length) return rescoreUnit({ ...unit, mains });
      if (unitNameMatch) return rescoreUnit({ ...unit, mains: unit.mains });
      return null;
    })
    .filter(Boolean);

  const filtering = Boolean(q || statusFilter || unitFilter);
  const isUnitOpen = (unit) => filtering || openUnits.has(unit.key);
  const isMainOpen = (main) => filtering || openMains.has(main.key);
  const toggleKey = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const totalMains = visibleUnits.reduce((s, u) => s + u.mains.length, 0);
  const totalSubs = visibleUnits.reduce(
    (s, u) => s + u.mains.reduce((n, m) => n + m.subs.length, 0),
    0,
  );

  const technicalStaffById = new Map(
    (Array.isArray(technicalData?.staff) ? technicalData.staff : []).map(
      (person) => [person._id, person],
    ),
  );

  const handleExportAll = async () => {
    if (!allReports.length) return;
    try {
      setExporting(true);
      await exportEmployeeReportsToExcel({
        employee,
        reports: allReports,
        technicalStaffById,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to export",
        text: "Something went wrong while generating the Excel workbook.",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Staff Reporting" motherMenu="Workforce" />
      {isSuperAdmin && allowOpen && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setAllowOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900">
              Allow past-date report
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {employee?.name || "This employee"} can add reports for the dates
              you pick (one day or a range). The permission expires in 48 hours.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  From date
                </label>
                <input
                  type="date"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                  value={allowDate}
                  max={todayKey}
                  min={toDateInputValue(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000))}
                  onChange={(e) => {
                    setAllowDate(e.target.value);
                    if (allowToDate && allowToDate < e.target.value)
                      setAllowToDate("");
                  }}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  To date (optional)
                </label>
                <input
                  type="date"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                  value={allowToDate}
                  max={todayKey}
                  min={allowDate || undefined}
                  onChange={(e) => setAllowToDate(e.target.value)}
                />
              </div>
            </div>
            <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Reason (optional)
            </label>
            <input
              type="text"
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
              value={allowNote}
              onChange={(e) => setAllowNote(e.target.value)}
            />
            {grants.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Currently allowed
                </div>
                <ul className="mt-1 divide-y divide-gray-100">
                  {grants.map((g) => (
                    <li
                      key={g._id}
                      className="flex items-center justify-between py-1.5 text-sm"
                    >
                      <span>{g.dateKey}</span>
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-600 hover:underline"
                        onClick={() => revokeGrant(g._id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600"
                onClick={() => setAllowOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="rounded-full bg-executive-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
                disabled={allowBusy}
                onClick={submitGrant}
              >
                {allowBusy ? "Saving…" : "Allow"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-6">
        <section className={`${panelClass} overflow-hidden`}>
          <div className="border-b border-gray-100 bg-gradient-to-br from-executive-50/60 via-white to-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-executive-600 text-lg font-semibold text-white shadow-lg shadow-executive-600/30">
                  {initials(employee?.name)}
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-executive-200 bg-executive-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-executive-700">
                    Employee Reports
                  </div>
                  <h1 className="mt-3 text-2xl font-semibold text-gray-900 sm:text-3xl">
                    {employee?.name || "Staff member"}
                  </h1>
                  <p className="mt-1 text-sm font-medium text-gray-500">
                    {formatRoleLabel(employee?.role)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isSuperAdmin && (
                  <button
                    type="button"
                    className="rounded-full border border-executive-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-executive-700 transition hover:bg-executive-50"
                    onClick={() => setAllowOpen(true)}
                  >
                    Allow past date{grants.length ? ` (${grants.length} active)` : ""}
                  </button>
                )}
                <button
                  type="button"
                  className="rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-600 transition hover:border-gray-400 hover:text-gray-900"
                  onClick={() => navigate("/staff-reporting")}
                >
                  Back to list
                </button>
                <button
                  type="button"
                  className="rounded-full bg-executive-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-md shadow-executive-600/30 transition hover:bg-executive-500 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleExportAll}
                  disabled={!allReports.length || exporting}
                >
                  {exporting ? "Preparing..." : "Download All (Excel)"}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-gray-100">
            <div className="px-6 py-5 text-center sm:text-left">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Total
              </div>
              <div className="mt-1.5 text-2xl font-semibold text-gray-900">
                {summary.total ?? allReports.length}
              </div>
            </div>
            <div className="px-6 py-5 text-center sm:text-left">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Completed
              </div>
              <div className="mt-1.5 text-2xl font-semibold text-emerald-600">
                {summary.completedCount ?? 0}
              </div>
            </div>
            <div className="px-6 py-5 text-center sm:text-left">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Avg Completion
              </div>
              <div className="mt-1.5 text-2xl font-semibold text-executive-600">
                {summary.averageCompletion ?? 0}%
              </div>
            </div>
          </div>
        </section>

        <section className={`${panelClass} p-5 md:p-6`}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
                All Work Sheets
              </h2>
              <p className="mt-0.5 text-xs text-gray-400">
                {visibleUnits.length} unit{visibleUnits.length === 1 ? "" : "s"}{" "}
                • {totalMains} main work{totalMains === 1 ? "" : "s"} •{" "}
                {totalSubs} sub-work{totalSubs === 1 ? "" : "s"}
              </p>
            </div>
            {isTechnicalEmployee && (
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                <select
                  value={unitFilter}
                  onChange={(event) => setUnitFilter(event.target.value)}
                  className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-executive-500 focus:ring-2 focus:ring-executive-500/20"
                >
                  <option value="">All units</option>
                  {unitOptions.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-executive-500 focus:ring-2 focus:ring-executive-500/20"
                >
                  <option value="">All statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <div className="relative w-full max-w-xs sm:w-56">
                  <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
                  <input
                    type="text"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search units, works, updates..."
                    className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-executive-500 focus:ring-2 focus:ring-executive-500/20"
                  />
                </div>
                {(unitFilter || statusFilter || searchText) && (
                  <button
                    type="button"
                    onClick={() => {
                      setUnitFilter("");
                      setStatusFilter("");
                      setSearchText("");
                    }}
                    className="rounded-xl border-2 border-executive-500 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-executive-50"
                  >
                    Reset
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  title="Reload work sheets"
                  className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60"
                >
                  <i
                    className={`fa-solid fa-rotate-right ${isFetching ? "fa-spin" : ""}`}
                  />
                  <span className="ml-1.5">Refresh</span>
                </button>
              </div>
            )}
          </div>

          {isLoading && (
            <div className="text-sm text-gray-500">Loading reports...</div>
          )}

          {!isTechnicalEmployee && !isLoading && allReports.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              No reports found for this employee yet.
            </div>
          )}

          {!isTechnicalEmployee && allReports.length > 0 && (
            <div className="space-y-2.5">
              {[...allReports]
                .sort(
                  (a, b) =>
                    new Date(lastRealUpdate(b) || b.reportDate || 0) -
                    new Date(lastRealUpdate(a) || a.reportDate || 0),
                )
                .map((report) => {
                  const note = latestNote(report);
                  const updatedAt = lastRealUpdate(report);
                  return (
                    <button
                      key={report._id}
                      type="button"
                      onClick={() =>
                        navigate(`/staff-reporting/${report._id}`)
                      }
                      className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-left transition hover:border-executive-500 hover:bg-executive-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-900">
                          {workSummary(report) || "Reporting sheet"}
                          {report.formData?.onBehalf?.managerName && (
                            <span className="ml-2 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200">
                              Added by {report.formData.onBehalf.managerName}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                          {updatedAt ? (
                            <span className="font-semibold text-executive-700">
                              Updated {formatDateLabel(updatedAt)}
                            </span>
                          ) : (
                            <span className="font-medium text-gray-400">
                              Not updated yet
                            </span>
                          )}
                          <span className="text-gray-400">
                            Created {formatDateLabel(report.reportDate)}
                          </span>
                        </div>
                        {note?.text && (
                          <div className="mt-1 truncate text-xs text-gray-500">
                            <span className="font-medium text-gray-600">
                              {formatDateLabel(note.date)}:
                            </span>{" "}
                            {note.text}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-3">
                        <span
                          className={`${cardStatusChip(report)} rounded-full px-3 py-1 text-xs font-semibold`}
                        >
                          {report.status ||
                            (report.isComplete ? "Complete" : "Pending")}
                        </span>
                        {!report.isComplete &&
                          reportHasOverdueWork(report.formData) && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-200">
                              <i className="fa-solid fa-triangle-exclamation text-[10px]" />
                              Overdue
                            </span>
                          )}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <div className="h-2 w-20 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-executive-500"
                              style={{
                                width: `${Math.min(100, Number(report.completionPercent) || 0)}%`,
                              }}
                            />
                          </div>
                          <span>
                            {Number(report.completionPercent) || 0}%
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
          )}

          {isTechnicalEmployee && !isLoading && visibleUnits.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              {filtering
                ? "No units, works or updates match the current filters."
                : "No reports found for this employee yet."}
            </div>
          )}

          {isTechnicalEmployee && visibleUnits.length > 0 && (
            <div className="space-y-3">
              {visibleUnits.map((unit) => {
                const unitOpen = isUnitOpen(unit);
                return (
                  <div
                    key={unit.key}
                    className="overflow-hidden rounded-2xl border border-gray-200"
                  >
                    {/* ── Unit ── */}
                    <button
                      type="button"
                      onClick={() => toggleKey(setOpenUnits, unit.key)}
                      className="flex w-full items-center gap-3 bg-gray-50 px-4 py-3 text-left transition hover:bg-executive-50/60"
                    >
                      <i
                        className={`fa-solid fa-chevron-right text-xs text-gray-400 transition ${unitOpen ? "rotate-90" : ""}`}
                      />
                      <span
                        className="flex-1 font-semibold"
                        style={{ fontSize: "16px", color: "#222222" }}
                      >
                        {unit.name}
                      </span>
                      <span className="hidden text-xs text-gray-400 sm:inline">
                        {unit.mains.length} main · {unit.subCount} sub
                      </span>
                      {unit.latestUpdate && (
                        <span className="hidden text-xs text-gray-400 md:inline">
                          Updated {formatDateLabel(unit.latestUpdate)}
                        </span>
                      )}
                      <span className="flex items-center gap-2 text-xs font-medium text-gray-500">
                        <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 sm:block">
                          <span
                            className={`block h-1.5 rounded-full ${completionBarColor(unit.completionPercent)}`}
                            style={{
                              width: `${Math.min(100, unit.completionPercent)}%`,
                            }}
                          />
                        </span>
                        {unit.completionPercent}%
                      </span>
                    </button>

                    {unitOpen && (
                      <div className="divide-y divide-gray-100 border-t border-gray-100">
                        {unit.mains.map((main) => {
                          const mainOpen = isMainOpen(main);
                          return (
                            <div key={main.key}>
                              {/* ── Main work ── */}
                              <button
                                type="button"
                                onClick={() =>
                                  toggleKey(setOpenMains, main.key)
                                }
                                className={`flex w-full items-center gap-3 px-4 py-2.5 pl-8 text-left transition ${
                                  isDoneStatus(main.status)
                                    ? "bg-emerald-50/70 hover:bg-emerald-50"
                                    : "hover:bg-gray-50"
                                }`}
                              >
                                <i
                                  className={`fa-solid fa-chevron-right text-[10px] text-gray-400 transition ${mainOpen ? "rotate-90" : ""}`}
                                />
                                <span className="flex-1 break-words font-medium text-gray-900">
                                  {main.name}
                                </span>
                                <span
                                  className={`${statusChip(shownStatus(main))} inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold`}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 rounded-full ${statusDot(shownStatus(main))}`}
                                  />
                                  {shownStatus(main)}
                                </span>
                                <OverdueBadge node={main} />
                                <ExtendedBadge node={main} />
                                <VerifiedBadge node={main} />
                                <WellDoneBadge node={main} />
                                {main.people > 0 && (
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                                    👥 {main.people} total
                                  </span>
                                )}
                                {main.latestUpdate && (
                                  <span className="hidden text-xs text-gray-400 sm:inline">
                                    {formatDateLabel(main.latestUpdate)}
                                  </span>
                                )}
                                <span className="w-10 flex-shrink-0 text-right text-xs font-medium text-gray-500">
                                  {main.completionPercent}%
                                </span>
                              </button>

                              {main.isOnHold && main.holdReason && (
                                <p className="bg-blue-50/50 px-4 pb-2 pl-14 text-xs text-blue-700">
                                  On hold: “{main.holdReason}”
                                </p>
                              )}

                              {mainOpen && (
                                <div className="space-y-3 bg-gray-50/50 px-4 py-3 pl-8">
                                  {main.subs.length === 0 && (
                                    <p className="text-xs text-gray-400">
                                      No sub-works recorded.
                                    </p>
                                  )}
                                  {main.subs.map((sub) => (
                                    <div
                                      key={sub.key}
                                      className={`rounded-xl border p-3 ${
                                        isDoneStatus(sub.status)
                                          ? "border-emerald-200 bg-emerald-50/70"
                                          : "border-gray-200 bg-white"
                                      }`}
                                    >
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-gray-900">
                                          {sub.name}
                                        </span>
                                        <span
                                          className={`${statusChip(shownStatus(sub))} inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold`}
                                        >
                                          <span
                                            className={`h-1.5 w-1.5 rounded-full ${statusDot(shownStatus(sub))}`}
                                          />
                                          {shownStatus(sub)}
                                        </span>
                                        <OverdueBadge node={sub} />
                                        <ExtendedBadge node={sub} />
                                        <VerifiedBadge node={sub} />
                                        <WellDoneBadge node={sub} />
                                        {sub.people > 0 && (
                                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                                            👥 {sub.people} people
                                          </span>
                                        )}
                                        {(sub.startDate || sub.completionDate) && (
                                          <span className="text-[11px] text-gray-400">
                                            {formatDateLabel(sub.startDate)} →{" "}
                                            {formatDateLabel(sub.completionDate)}
                                          </span>
                                        )}
                                      </div>
                                      {sub.isOnHold && (
                                        <p className="mt-1 text-xs font-medium text-blue-700">
                                          On hold
                                          {sub.holdDate
                                            ? ` since ${formatDateLabel(sub.holdDate)}`
                                            : ""}
                                          {sub.holdReason
                                            ? ` — “${sub.holdReason}”`
                                            : " — no reason recorded"}
                                        </p>
                                      )}
                                      {sub.remarks && (
                                        <p className="mt-1 text-xs text-gray-500">
                                          {sub.remarks}
                                        </p>
                                      )}
                                      {sub.updates.length > 0 ? (
                                        <ul className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
                                          {sub.updates.map((u, ui) => {
                                            const uKey = `${u.reportId}::${u.subId}::${u.date}::${u.peopleCount}::${u.text}`;
                                            const editable =
                                              canEditUpdates &&
                                              (isSuperAdmin || !u.reportComplete);
                                            if (updateEdit?.key === uKey) {
                                              return (
                                                <li
                                                  key={ui}
                                                  className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 p-2 text-xs"
                                                >
                                                  <input
                                                    type="date"
                                                    className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-gray-900 placeholder:text-gray-400"
                                                    value={updateEdit.date}
                                                    max={todayKey}
                                                    onChange={(e) =>
                                                      setUpdateEdit((p) => ({
                                                        ...p,
                                                        date: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    className="w-20 rounded-lg border border-gray-300 bg-white px-2 py-1 text-gray-900 placeholder:text-gray-400"
                                                    placeholder="People"
                                                    value={updateEdit.people}
                                                    onChange={(e) =>
                                                      setUpdateEdit((p) => ({
                                                        ...p,
                                                        people: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                  <input
                                                    className="min-w-[200px] flex-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-gray-900 placeholder:text-gray-400"
                                                    placeholder="Progress note"
                                                    value={updateEdit.text}
                                                    onChange={(e) =>
                                                      setUpdateEdit((p) => ({
                                                        ...p,
                                                        text: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                  <button
                                                    type="button"
                                                    className="rounded-full border border-gray-300 px-3 py-1 font-semibold text-gray-600"
                                                    onClick={() => setUpdateEdit(null)}
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="rounded-full bg-amber-500 px-3 py-1 font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
                                                    disabled={updateSaving}
                                                    onClick={() => saveWorkUpdate(u)}
                                                  >
                                                    {updateSaving ? "Saving..." : "Save"}
                                                  </button>
                                                </li>
                                              );
                                            }
                                            return (
                                              <li
                                                key={ui}
                                                className="group flex gap-2 text-xs text-gray-600"
                                              >
                                                <span className="w-24 flex-shrink-0 font-semibold text-gray-500">
                                                  {formatDateLabel(u.date)}
                                                </span>
                                                <span className="flex-1 break-words">
                                                  {u.peopleCount !== "" && (
                                                    <span className="font-medium text-gray-500">
                                                      👥 {u.peopleCount}
                                                      {u.text ? " — " : ""}
                                                    </span>
                                                  )}
                                                  {u.text}
                                                </span>
                                                {editable && (
                                                  <span className="flex flex-shrink-0 gap-3 font-semibold">
                                                    <button
                                                      type="button"
                                                      className="text-amber-700 hover:text-amber-800"
                                                      disabled={updateSaving}
                                                      onClick={() =>
                                                        setUpdateEdit({
                                                          key: uKey,
                                                          date: String(u.date || "").slice(0, 10),
                                                          text: u.text || "",
                                                          people:
                                                            u.peopleCount === "" ||
                                                            u.peopleCount == null
                                                              ? ""
                                                              : String(u.peopleCount),
                                                        })
                                                      }
                                                    >
                                                      Edit
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="text-red-600 hover:text-red-700"
                                                      disabled={updateSaving}
                                                      onClick={() =>
                                                        saveWorkUpdate(u, { remove: true })
                                                      }
                                                    >
                                                      Delete
                                                    </button>
                                                  </span>
                                                )}
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      ) : (
                                        <p className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-gray-400">
                                          No updates yet.
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      navigate(
                                        `/staff-reporting/${main.reportId}`,
                                      )
                                    }
                                    className="text-xs font-semibold text-executive-600 transition hover:text-executive-700"
                                  >
                                    Open sheet detail →
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

    </>
  );
};

export default EmployeeStaffReports;
